import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { mockTestimonials } from '@/mocks/travel';

gsap.registerPlugin(ScrollTrigger);

export default function TestimonialsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const total = mockTestimonials.length;

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, 5000);
    return () => clearInterval(intervalRef.current);
  }, [total]);

  const goTo = (index: number) => {
    setCurrent(index);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, 5000);
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        sectionRef.current,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.6,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="relative w-full py-24 md:py-32 px-6 md:px-10 lg:px-16 bg-background-50">
      <div className="max-w-[1440px] mx-auto">
        <div className="text-center mb-16 md:mb-20">
          <span className="inline-block text-xs tracking-[0.2em] uppercase text-primary-500 font-label mb-4">
            Depoimentos
          </span>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-foreground-900 leading-tight">
            Quem já viajou <span className="italic">conosco</span>
          </h2>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl bg-background-50 border border-background-200/50">
            <div
              ref={trackRef}
              className="flex transition-transform duration-700 ease-out"
              style={{ transform: `translateX(-${current * 100}%)` }}
            >
              {mockTestimonials.map((t, i) => (
                <div key={i} className="w-full flex-shrink-0">
                  <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 p-8 md:p-12">
                    {/* Photo */}
                    <div className="w-24 h-24 md:w-32 md:h-32 flex-shrink-0 rounded-full overflow-hidden">
                      <img
                        src={t.image}
                        alt={t.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>

                    {/* Text */}
                    <div className="flex-1 text-center md:text-left">
                      <div className="flex items-center justify-center md:justify-start gap-1 mb-3">
                        {[...Array(t.rating)].map((_, idx) => (
                          <i key={idx} className="ri-star-fill text-accent-500 text-sm"></i>
                        ))}
                      </div>
                      <blockquote className="text-foreground-700 text-base md:text-lg leading-relaxed italic mb-4">
                        "{t.text}"
                      </blockquote>
                      <div>
                        <p className="font-heading text-xl font-medium text-foreground-900">
                          {t.name}
                        </p>
                        <p className="text-foreground-500 text-sm">
                          {t.location} — {t.destination}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation dots */}
            <div className="flex justify-center gap-2 pb-6">
              {mockTestimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`w-2 h-2 rounded-full transition-all duration-500 cursor-pointer ${
                    i === current
                      ? 'bg-accent-500 w-6'
                      : 'bg-background-300 hover:bg-background-400'
                  }`}
                  aria-label={`Depoimento ${i + 1}`}
                ></button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}